import { PlanEntitlement } from '../services/api';

/**
 * Read-only Anzeige der Plan-Entitlements (Sizing, User-Caps, Backup,
 * Isolation) fuer einen gewaehlten Plan. Wird im Create-Tenant-Wizard
 * unter der Plan-Auswahl und im Orders-Approve-Modal verwendet, damit
 * der Operator sieht, *was* ein Plan provisioniert, bevor er ihn waehlt
 * bzw. freigibt.
 *
 * Datenquelle: GET /api/plans (plan.routes.ts). Diese Komponente
 * rendert nur — sie laedt nicht selbst; der Aufrufer reicht den bereits
 * geladenen Katalog herein (ein Fetch pro Seite).
 */

function formatBytes(n: number | null): string {
  if (n === null) return 'unbegrenzt';
  const GiB = 1024 ** 3;
  const TiB = 1024 ** 4;
  if (n >= TiB) return `${+(n / TiB).toFixed(n % TiB === 0 ? 0 : 1)} TiB`;
  if (n >= GiB) return `${+(n / GiB).toFixed(n % GiB === 0 ? 0 : 1)} GiB`;
  return `${n} B`;
}

function formatCount(n: number | null): string {
  return n === null ? 'unbegrenzt' : n.toLocaleString('de-DE');
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

export function PlanEntitlements({
  plan,
  plans,
  className,
}: {
  plan: string;
  plans: PlanEntitlement[];
  className?: string;
}) {
  const p = plans.find((x) => x.name === plan);
  if (!p) {
    // Katalog noch nicht geladen oder unbekannter Plan — still bleiben,
    // statt eine leere Box zu zeigen.
    return null;
  }

  const isolated = p.isolation.mode === 'isolated';

  return (
    <div className={`border rounded p-3 bg-slate-50 text-sm ${className ?? ''}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-slate-500">Umgebung &amp; Entitlements</p>
        <span
          className={`badge ${isolated ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-200 text-slate-700'}`}
          title={
            isolated
              ? 'Dedizierter vcluster + eigenes Keycloak-Realm + dedizierter, getainteter Node-Pool'
              : 'Namespace im geteilten „sectepe"-Realm (kein eigener vcluster)'
          }
        >
          {isolated ? 'Dediziert' : 'Shared-Realm'}
        </span>
      </div>

      <p className="text-slate-600 mb-3">{p.description}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Sizing</p>
          <Row label="CPU (Request/Limit)" value={`${p.quota.requests_cpu} / ${p.quota.limits_cpu}`} mono />
          <Row label="RAM (Request/Limit)" value={`${p.quota.requests_memory} / ${p.quota.limits_memory}`} mono />
          <Row label="Storage" value={p.quota.storage} mono />
          <Row label="PVCs" value={p.quota.pvc_count} mono />
          <Row label="LoadBalancer / NodePort" value={`${p.quota.services_loadbalancer} / ${p.quota.services_nodeport}`} mono />
          <Row label="vcluster-Replicas" value={p.isolation.vclusterReplicas} mono />
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 mt-3 sm:mt-0">
            Nutzung &amp; Limits
          </p>
          <Row label="Max. Benutzer" value={formatCount(p.usage.active_users)} mono />
          <Row label="Mail-Speicher" value={formatBytes(p.usage.mail_storage_bytes)} mono />
          <Row label="Index-Speicher" value={formatBytes(p.usage.index_storage_bytes)} mono />
          <Row label="Mail/Periode" value={formatCount(p.usage.mail_msgs)} mono />
          <Row label="Suchen/Periode" value={formatCount(p.usage.search_queries)} mono />
          <Row
            label="Quota-Enforcement"
            value={p.usage.enforced ? 'hart' : 'weich (Warnung)'}
          />
        </div>
      </div>

      <div className="mt-3 pt-2 border-t text-slate-600">
        <Row
          label="Backup"
          value={`Retention ${p.backup.velero_ttl} · Drill ${p.backup.drill_schedule}`}
          mono
        />
      </div>
    </div>
  );
}
